import axios from 'axios';
import { LoginCredentials, VerifyCredentials, AuthResponse } from '../types/auth';

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:5321'
    : (window.location.hostname === 'new.eazzy.pro' || window.location.hostname === 'eazzy.pro'
        ? 'https://api.eazzy.pro'
        : 'https://api-eazzy.pomuc.ru');

// Создаем экземпляр axios с базовой конфигурацией
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерсептор для добавления токена к запросам
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Интерсептор для обработки ошибок авторизации
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Токен истек, пытаемся обновить
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      const userType = localStorage.getItem('user_type'); // 'user' или 'manager'
      
      if (accessToken && refreshToken) {
        try {
          let response;
          if (userType === 'manager') {
            response = await authService.refreshManagerToken(accessToken, refreshToken);
          } else {
            response = await authService.refreshToken(accessToken, refreshToken);
          }
          
          if (response.accessToken) {
            localStorage.setItem('access_token', response.accessToken);
            localStorage.setItem('refresh_token', response.refreshToken!);
            
            // Повторяем оригинальный запрос
            const originalRequest = error.config;
            originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          // Не удалось обновить токен, перенаправляем на авторизацию
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('auth_user');
          localStorage.removeItem('user_type');
          window.location.href = '/auth';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // Авторизация исполнителя (пользователя) через ФНС
  async loginUser(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiClient.post('/api/v1/auth', credentials);
    return response.data;
  },

  // Подтверждение кода для исполнителя
  async verifyUser(credentials: VerifyCredentials): Promise<AuthResponse> {
    const response = await apiClient.post('/api/v1/auth/verify', credentials);
    return response.data;
  },

  // Авторизация менеджера/бизнеса через MTS ID
  async loginManager(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiClient.post('/api/v1/manager/auth', credentials);
    return response.data;
  },

  // Подтверждение авторизации менеджера после редиректа с MTS
  async verifyManager(credentials: VerifyCredentials): Promise<AuthResponse> {
    const response = await apiClient.post('/api/v1/manager/auth/verify', credentials);
    return response.data;
  },

  // Обновление токена для исполнителя
  async refreshToken(accessToken: string, refreshToken: string): Promise<AuthResponse> {
    const response = await apiClient.post('/api/v1/auth/refresh', {
      accessToken: accessToken,
      refreshToken: refreshToken
    });
    return response.data;
  },

  // Обновление токена для менеджера
  async refreshManagerToken(accessToken: string, refreshToken: string): Promise<AuthResponse> {
    const response = await apiClient.post('/api/v1/auth/refresh', {
      accessToken: accessToken,
      refreshToken: refreshToken
    });
    return response.data;
  },

  // Получение информации о текущем пользователе
  async getCurrentUser(): Promise<any> {
    const response = await apiClient.get('/api/v1/user/me');
    return response.data;
  },

  // Выход (отзыв токена)
  async logout(): Promise<void> {
    try {
      await apiClient.post('/api/v1/auth/logout');
    } catch (error) {
      // Игнорируем ошибки при выходе
      console.warn('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('user_type');
    }
  }
};

export { apiClient };